import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { EcommerceBannerPlacement, EcommerceResponse, IEcommerceQuestionSchema } from '../../types.ts'
import style from './Main.module.scss'
import Banner from './Banner.tsx'
import ProductCard from './ProductCard.tsx'
import { countDrawnCards } from '../../utils/hiddenClass.ts'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  findability: boolean
  registerCardRef: (id: AnswerID, element: HTMLDivElement | null) => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number) => void
}

const FILTERS = [
  'filterPrice',
  'filterDepartments',
  'filterProductType',
  'filterBrand',
  'filterSavings',
  'filterProductRating',
  'filterNewItems'
]
const CATEGORIES = [
  'catShopAllSavings',
  'catLaborDaySavings',
  'catFallFavorites',
  'catExpressDelivery',
  'catNewLowerPrice'
]

const Main = ({ schema, response, findability, registerCardRef, onItemSelect, onAddToCart }: IMainProps) => {
  const { t } = useSurveyLocale()
  const viewport = useViewport()
  const bannerProducts = schema.answers.filter(answer => answer.banner && !answer.na)
  const products = schema.answers.filter(answer => !answer.na && !answer.parent_product)
  // the header counts the cards the grid draws: a preview-hidden card is not drawn
  const productsCount = countDrawnCards(products)
  const productsPerRow = schema.products_per_row || 5

  const banner = bannerProducts.length > 0 && (
    <Banner
      schema={schema}
      bannerProducts={bannerProducts}
      response={response}
      findability={findability}
      onItemSelect={onItemSelect}
      onAddToCart={onAddToCart}
    />
  )

  const resultsTitle = (
    <div className={style.resultsTitle}>
      <span className={style.searchTerm} dangerouslySetInnerHTML={{ __html: `"${schema.search_condition || ''}"` }} />
      <span className={style.resultsCount}>
        {t<(count: number) => string>('questions', 'ec', 'samsclub', 'results')(productsCount)}
      </span>
    </div>
  )

  const pricesNote = (
    <span className={style.pricesNote}>
      {t('questions', 'ec', 'samsclub', 'pricesMayVary')}
      <EmptyLink color="#0a6cff">
        <i className="pi pi-info-circle" />
      </EmptyLink>
    </span>
  )

  const categories = CATEGORIES.map(key => (
    <EmptyLink key={key} color="#0a6cff">
      <span className={style.categoryChip}>{t('questions', 'ec', 'samsclub', key)}</span>
    </EmptyLink>
  ))

  const renderCard = (answer: (typeof products)[number]) => (
    <ProductCard
      key={answer.id}
      data-testid="SW-EC-Item"
      schema={schema}
      answer={answer}
      amount={response.answers[answer.index].v}
      findability={findability}
      disabled={'focus' == schema.mode_type && !answer.focused}
      registerCardRef={registerCardRef}
      onSelect={() => onItemSelect(answer.index)}
      onAdd={qnt => onAddToCart(answer.index, qnt)}
    />
  )

  if (viewport.mobile) {
    return (
      <main className={style.mainMobile} data-testid="SW-EC-Main">
        {banner}
        <div className={style.mobileResults}>
          <div className={style.mobileResultsText}>
            {resultsTitle}
            {pricesNote}
          </div>
          <EmptyLink color="#0a6cff">
            <span className={style.filterButton}>
              <i className="ld-Sliders" />
            </span>
          </EmptyLink>
        </div>

        <div className={`${style.categories} ${style.categoriesMobile}`}>{categories}</div>

        <div className={style.list} data-testid="SW-EC-Grid">
          {products.map(renderCard)}
        </div>
      </main>
    )
  }

  return (
    <main className={style.main} data-testid="SW-EC-Main">
      {banner}

      <div className={style.resultsRow}>
        {resultsTitle}
        {pricesNote}
      </div>

      <div className={style.filtersRow}>
        <div className={style.filters}>
          <EmptyLink color="#0a6cff">
            <span className={style.filterPill}>
              <i className={`pi pi-sliders-h ${style.filterIcon}`} />
              {t('questions', 'ec', 'samsclub', 'filterAll')}
            </span>
          </EmptyLink>
          {FILTERS.map(key => (
            <EmptyLink key={key} color="#0a6cff">
              <span className={style.filterPill}>
                {t('questions', 'ec', 'samsclub', key)}
                <i className={`pi pi-chevron-down ${style.filterChevron}`} />
              </span>
            </EmptyLink>
          ))}
        </div>
        <EmptyLink color="#0a6cff">
          <span className={style.sort}>
            {t('questions', 'ec', 'samsclub', 'sortBy')}
            <span className={style.sortDivider}>|</span>
            {t('questions', 'ec', 'samsclub', 'sortRelevance')}
            <i className={`pi pi-chevron-down ${style.filterChevron}`} />
          </span>
        </EmptyLink>
      </div>

      <div className={style.categories}>{categories}</div>

      <div
        className={style.grid}
        style={{ gridTemplateColumns: `repeat(${productsPerRow}, minmax(0, 1fr))` }}
        data-testid="SW-EC-Grid"
      >
        {products.map(renderCard)}
      </div>
    </main>
  )
}

export default Main
